import { Form } from "@unform/web";
import {
  Building,
  CircleDollarSign,
  Files,
  FileSignature,
  MailPlus,
  Pencil,
  Settings2,
  Trash,
  X,
} from "lucide-react";
import React, {
  useEffect,
  useRef,
  useState,
  createContext,
  useContext,
} from "react";
import Input from "~/components/RegisterForm/Input";
import RegisterFormMenu from "~/components/RegisterForm/Menu";
import Textarea from "~/components/RegisterForm/Textarea";
import CheckboxInput from "~/components/RegisterForm/CheckboxInput";
import api from "~/services/api";
import {
  countries_list,
  getRegistries,
  getTabsPermissions,
  handleUpdatedFields,
  tabAllowed,
} from "~/functions";
import { Zoom, toast } from "react-toastify";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import FormHeader from "~/components/RegisterForm/FormHeader";
import { Scope } from "@unform/core";
import Preview from "~/components/Preview";
import SelectPopover from "~/components/RegisterForm/SelectPopover";
import FormLoading from "~/components/RegisterForm/FormLoading";
import FileInput from "~/components/RegisterForm/FileInput";
import { organizeMultiAndSingleFiles } from "~/functions/uploadFile";
import { AlertContext } from "~/App";
import {
  discountOptions,
  discountTypesOptions,
  yesOrNoOptions,
  appliedAtOptions,
  periodOptions,
} from "~/functions/selectPopoverOptions";
import { FullGridContext } from "../..";
import PhoneNumberInput from "~/components/RegisterForm/PhoneNumberInput";
import FindGeneric from "~/components/Finds/FindGeneric";
import PlanScheduleSubstatusModal from "./PlanScheduleSubstatusModal";

export const InputContext = createContext({});

const BRANCH_TABS = [
  { name: "general", permissionAlias: "general-tab" },
  { name: "shifts", permissionAlias: "shifts-tab" },
  { name: "plan-schedule", permissionAlias: "plan-schedule-tab" },
  {
    name: "price-list",
    permissionAlias: "price-list-tab",
    requiresExistingBranch: true,
  },
  {
    name: "discount-list",
    permissionAlias: "discount-list-tab",
    requiresExistingBranch: true,
  },
  {
    name: "financial-support",
    permissionAlias: "financial-support-tab",
    requiresExistingBranch: true,
  },
  {
    name: "contracts",
    permissionAlias: "contracts-tab",
    requiresExistingBranch: true,
  },
  {
    name: "contact-emails",
    permissionAlias: "contact-emails-tab",
    requiresExistingBranch: true,
  },
  {
    name: "params",
    permissionAlias: "params-tab",
    requiresExistingBranch: true,
  },
];

export default function PagePreview({
  access,
  id,
  defaultFormType = "preview",
}) {
  const {
    handleOpened,
    setOpened,
    successfullyUpdated,
    setSuccessfullyUpdated,
  } = useContext(FullGridContext);
  const tabsPermissions = getTabsPermissions("filials", FullGridContext);
  const permittedTabs = BRANCH_TABS.filter((tab) =>
    tabAllowed(tabsPermissions, tab.permissionAlias)
  );
  const availableTabs = permittedTabs.filter(
    (tab) => id !== "new" || !tab.requiresExistingBranch
  );
  const availableTabNames = availableTabs.map((tab) => tab.name);
  const availableTabsKey = availableTabNames.join("|");
  const [pageData, setPageData] = useState({
    active: false,
    alias: "",
    name: "",
    Filialtype: { id: null, label: "" },
    administrator: null,
    ein: "",
    address: "",
    zipcode: "",
    city: "",
    state: "",
    country: "",
    observations: "",
    pricelists: [],
    discountlists: [],
    shift_hours: [],
    plan_schedules: [],
    loaded: false,
    parking_spot_image: null,
    dso_name: "",
    i17_school_name: "",
    dso_signature: null,
    financial_support_student_amount: 0,
    financial_support_dependent_amount: 0,
    financial_support_year_amount: 0,
    registration_number_mode: null,
    settlement_range_date: null,
    overdue_invoice_limit: 2,
    price_by_hour: null,
  });
  const [formType, setFormType] = useState(defaultFormType);
  const [fullscreen, setFullscreen] = useState(false);
  const [activeMenu, setActiveMenu] = useState(
    () => availableTabNames[0] || null
  );

  const [registry, setRegistry] = useState({
    created_by: null,
    created_at: null,
    updated_by: null,
    updated_at: null,
    canceled_by: null,
    canceled_at: null,
  });
  const [filialTypesOptions, setFilialTypesOptions] = useState([]);
  const [processSubstatusOptions, setProcessSubstatusOptions] = useState([]);
  const [substatusModalIndex, setSubstatusModalIndex] = useState(null);
  const generalForm = useRef();
  const { alertBox } = useContext(AlertContext);
  const [loading, setLoading] = useState(false);
  const [overdueInvoiceBlockEnabled, setOverdueInvoiceBlockEnabled] =
    useState(true);

  const hasTabPermission = (permissionAlias) =>
    Boolean(tabAllowed(tabsPermissions, permissionAlias));

  const canOpenTab = (name) => availableTabNames.includes(name);

  const tabDisabledMessage = (permissionAlias, existingBranchMessage) => {
    if (!hasTabPermission(permissionAlias)) {
      return "You don't have permission to access this tab.";
    }

    return existingBranchMessage;
  };

  useEffect(() => {
    if (!canOpenTab(activeMenu)) {
      setActiveMenu(availableTabNames[0] || null);
    }
  }, [activeMenu, availableTabsKey]);

  const countriesOptions = countries_list.map((country) => {
    return { value: country, label: country };
  });

  useEffect(() => {
    async function getPageData() {
      try {
        api.get(`filials/${id}`).then(async ({ data }) => {
          console.log(
            "GET plan_schedules is_global:",
            (data.plan_schedules || []).map((plan) => ({
              id: plan.id,
              is_global: plan.is_global,
              type: typeof plan.is_global,
              processsubstatuses: (plan.processsubstatuses || []).map(
                (processsubstatus) => processsubstatus.id
              ),
            }))
          );
          api
            .get(`processsubstatuses`)
            .then(async ({ data: processsubstatuses }) => {
              const { data: documents } = await api.get(
                `/documentsByOrigin?origin=Branches&type=Contracts`
              );
              const mappedProcessSubstatuses = processsubstatuses.rows.map(
                (processsubstatus) => {
                  return {
                    id: null,
                    name: processsubstatus.name,
                    processsubstatus_id: processsubstatus.id,
                    registration_fee: 0,
                    book: 0,
                    tuition: 0,
                    active: false,
                  };
                }
              );
              const {
                created_by,
                created_at,
                updated_by,
                updated_at,
                canceled_by,
                canceled_at,
                pricelists,
                plan_schedules,
              } = data;
              setProcessSubstatusOptions(
                processsubstatuses.rows.map((processsubstatus) => ({
                  id: processsubstatus.id,
                  name: processsubstatus.name,
                }))
              );
              setPageData({
                ...data,
                documents,
                loaded: true,
                plan_schedules: (plan_schedules || []).map((plan) => ({
                  ...plan,
                  is_global:
                    plan.is_global === true ||
                    plan.is_global === "true" ||
                    plan.is_global === 1,
                  processsubstatus_ids: (plan.processsubstatuses || []).map(
                    (processsubstatus) => processsubstatus.id
                  ),
                })),
                pricelists: pricelists.concat(
                  mappedProcessSubstatuses.filter(
                    (processsubstatus) =>
                      !pricelists.find(
                        (price) =>
                          price.processsubstatus_id ===
                          processsubstatus.processsubstatus_id
                      )
                  )
                ),
              });
              setOverdueInvoiceBlockEnabled(
                data.overdue_invoice_block_enabled === false ? false : true
              );
              const registries = await getRegistries({
                created_by,
                created_at,
                updated_by,
                updated_at,
                canceled_by,
                canceled_at,
              });
              setRegistry(registries);
            });
        });
      } catch (err) {
        if (err?.response?.data?.error) {
          toast(err.response.data.error, { type: "error", autoClose: 3000 });
        }
      }
    }

    async function getProcessSubstatusOptions() {
      try {
        const { data } = await api.get(`processsubstatuses`);
        setProcessSubstatusOptions(
          (data?.rows || []).map((processsubstatus) => ({
            id: processsubstatus.id,
            name: processsubstatus.name,
          }))
        );
      } catch (err) {
        if (err?.response?.data?.error) {
          toast(err.response.data.error, { type: "error", autoClose: 3000 });
        }
      }
    }

    if (id === "new") {
      setFormType("full");
      getProcessSubstatusOptions();
    } else if (id) {
      getPageData();
    }
  }, []);

  async function handleGeneralFormSubmit(data) {
    setLoading(true);

    const parseSettlementRangeDate = (value) => {
      if (value === null || typeof value === "undefined" || value === "") {
        return null;
      }

      if (typeof value === "number") {
        return Number.isInteger(value) ? value : null;
      }

      const normalized = String(value).trim();
      if (!/^\d+$/.test(normalized)) {
        return null;
      }

      return Number.parseInt(normalized, 10);
    };

    const hasSettlementRangeInput =
      !(data.settlement_range_date === null ||
        typeof data.settlement_range_date === "undefined" ||
        data.settlement_range_date === "");
    const parsedSettlementRangeDate = parseSettlementRangeDate(
      data.settlement_range_date
    );

    if (hasSettlementRangeInput && parsedSettlementRangeDate === null) {
      toast("Settlement Range Date must be an integer.", {
        type: "error",
        autoClose: 3000,
      });
      setLoading(false);
      return;
    }

    data.settlement_range_date = parsedSettlementRangeDate;

    if (successfullyUpdated) {
      toast("No need to be saved!", {
        autoClose: 1000,
        type: "info",
        transition: Zoom,
      });
      setLoading(false);
      return;
    }
    if (data.shift_hours && data.shift_hours.length > 0) {
      data.shift_hours = data.shift_hours.filter((shift) => {
        if (!shift) {
          return false;
        }
        const { name, start_time, end_time } = shift;
        return name || start_time || end_time;
      });
    }
    if (data.plan_schedules && data.plan_schedules.length > 0) {
      const validPlanSchedules = [];

      for (let i = 0; i < data.plan_schedules.length; i++) {
        const plan = data.plan_schedules[i];
        if (!plan) continue;

        const { period, start_time, end_time, monday, tuesday, wednesday, thursday, friday, saturday } = plan;

        const hasAnyField = period || start_time || end_time || monday || tuesday || wednesday || thursday || friday || saturday;

        if (hasAnyField) {
          if (!start_time || !end_time) {
            toast("Plan Schedule: Start time and end time are required for all schedules", {
              type: "error",
              autoClose: 3000,
            });
            setLoading(false);
            return;
          }

          console.log("Plan days:", { monday, tuesday, wednesday, thursday, friday, saturday });
          const hasAtLeastOneDay =
            (monday === true || monday === "true") ||
            (tuesday === true || tuesday === "true") ||
            (wednesday === true || wednesday === "true") ||
            (thursday === true || thursday === "true") ||
            (friday === true || friday === "true") ||
            (saturday === true || saturday === "true");

          if (!hasAtLeastOneDay) {
            toast("Plan Schedule: At least one day must be selected for each schedule", {
              type: "error",
              autoClose: 3000,
            });
            setLoading(false);
            return;
          }

          // is_global and processsubstatus_ids live in pageData (state), not
          // in the unform fields. Match the state plan by id (or index as a
          // fallback for brand-new rows) instead of relying on index only,
          // since add inserts at the top and removals can shift positions.
          const statePlan =
            (plan.id &&
              pageData.plan_schedules.find(
                (statePlan) => statePlan.id === plan.id
              )) ||
            pageData.plan_schedules[i] ||
            {};
          const isGlobal = statePlan.is_global ?? false;
          validPlanSchedules.push({
            ...plan,
            is_global: isGlobal,
            processsubstatus_ids: isGlobal
              ? []
              : statePlan.processsubstatus_ids || [],
          });
        }
      }

      data.plan_schedules = validPlanSchedules;
      console.log(
        "plan_schedules payload:",
        JSON.stringify(
          validPlanSchedules.map((plan) => ({
            id: plan.id,
            is_global: plan.is_global,
            processsubstatus_ids: plan.processsubstatus_ids,
          })),
          null,
          2
        )
      );
    }
    if (data.parking_spot_image) {
      const allPromises = organizeMultiAndSingleFiles(
        [{ file_id: data.parking_spot_image, document_id: null }],
        "Branches/Parking Spot"
      );
      Promise.all(allPromises).then(async (files) => {
        await api.put(`/filials/${id}`, {
          parking_spot_image: files[0],
        });
      });
      delete data.parking_spot_image;
    }
    if (data.dso_signature) {
      const allPromises = organizeMultiAndSingleFiles(
        [{ file_id: data.dso_signature, document_id: null }],
        "Branches/Signatures"
      );
      Promise.all(allPromises).then(async (files) => {
        await api.put(`/filials/${id}`, {
          dso_signature: files[0],
        });
      });
      delete data.dso_signature;
    }
    if (data.student_handbook) {
      const allPromises = organizeMultiAndSingleFiles(
        [{ file_id: data.student_handbook, document_id: null }],
        "Branches/Parking Spot"
      );
      Promise.all(allPromises).then(async (files) => {
        await api.put(`/filials/${id}`, {
          student_handbook: files[0],
        });
      });
      delete data.student_handbook;
    }
    if (data.mila_app_first_access) {
      const allPromises = organizeMultiAndSingleFiles(
        [{ file_id: data.mila_app_first_access, document_id: null }],
        "Branches/Parking Spot"
      );
      Promise.all(allPromises).then(async (files) => {
        await api.put(`/filials/${id}`, {
          mila_app_first_access: files[0],
        });
      });
      delete data.mila_app_first_access;
    }
    if (id === "new") {
      try {
        let {
          financial_support_student_amount = 0,
          financial_support_dependent_amount = 0,
          financial_support_year_amount = 0,
        } = data;

        if (financial_support_student_amount === "0") {
          financial_support_student_amount = 0;
        }

        if (financial_support_dependent_amount === "0") {
          financial_support_dependent_amount = 0;
        }

        if (financial_support_year_amount === "0") {
          financial_support_year_amount = 0;
        }

        const response = await api.post(`/filials`, {
          ...data,
          financial_support_student_amount,
          financial_support_dependent_amount,
          financial_support_year_amount,
        });
        setOpened(response.data.id);
        setPageData({ ...pageData, ...data });
        setSuccessfullyUpdated(true);
        toast("Saved!", { autoClose: 1000 });
        handleOpened(null);
        setLoading(false);
      } catch (err) {
        if (err?.response?.data?.error) {
          toast(err.response.data.error, { type: "error", autoClose: 3000 });
        }
        setLoading(false);
        return;
      }
    } else if (id !== "new") {
      const updated = handleUpdatedFields(data, pageData);

      // Substatus links and is_global live in state, so handleUpdatedFields
      // may not catch a change that only touched them. Compare the merged
      // plan_schedules against what was loaded and force the array into the
      // payload when it differs, so the links always persist.
      const planSubstatusSignature = (schedules = []) =>
        JSON.stringify(
          (schedules || []).map((plan) => ({
            id: plan.id || null,
            is_global: plan.is_global ?? false,
            processsubstatus_ids: [
              ...(plan.processsubstatus_ids || []),
            ].sort((a, b) => a - b),
          }))
        );
      const planSchedulesChanged =
        planSubstatusSignature(data.plan_schedules) !==
        planSubstatusSignature(pageData.plan_schedules);

      if (updated.length > 0 || planSchedulesChanged) {
        const objUpdated = Object.fromEntries(updated);
        // Always send the merged plan_schedules (with is_global /
        // processsubstatus_ids) whenever they're part of the payload, since
        // the diff version from handleUpdatedFields lacks the state fields.
        if (
          (planSchedulesChanged || objUpdated.plan_schedules) &&
          data.plan_schedules
        ) {
          objUpdated.plan_schedules = data.plan_schedules;
        }
        if (data.documents && data.documents.length > 0) {
          let toastId = null;
          if (
            data.documents.find(
              (document) =>
                (typeof document.file_id === "undefined" && document.file_id) ||
                (typeof document.file_id === "object" &&
                  Array.from(document.file_id).length > 0)
            )
          ) {
            toastId = toast.loading("Files are being uploaded...");
          }
          const allPromises = organizeMultiAndSingleFiles(
            data.documents,
            "Branches"
          );
          Promise.all(allPromises).then(async (files) => {
            try {
              files.map(async (file) => {
                if (!file) {
                  return;
                }
                if (file.name) {
                  api.post(`/filialdocuments`, { filial_id: id, files: file });
                  toastId &&
                    toast.update(toastId, {
                      render: "All files have been uploaded!",
                      type: "success",
                      autoClose: 3000,
                      isLoading: false,
                    });
                } else {
                  file
                    .sort((a, b) => a.size > b.size)
                    .map(async (promise, index) => {
                      await Promise.all([promise]).then(async (singleFile) => {
                        // console.log(singleFile[0])
                        if (index + 1 === file.length) {
                          toastId &&
                            toast.update(toastId, {
                              render: "All files have been uploaded!",
                              type: "success",
                              autoClose: 3000,
                              isLoading: false,
                            });
                        }
                        await api.post(`/filialdocuments`, {
                          filial_id: id,
                          files: singleFile[0],
                        });
                      });
                    });
                }
              });
            } catch (err) {
              console.log(err);
              setLoading(false);
              return;
              // toast(err.response.data.error, { type: 'error', autoClose: 3000 })
            }
            delete objUpdated.documents;

            await api.put(`/filials/${id}`, objUpdated);
            setPageData({ ...pageData, ...objUpdated });
            setSuccessfullyUpdated(true);
            toast("Saved!", { autoClose: 1000 });
            handleOpened(null);
            setLoading(false);
          });
        } else {
          try {
            delete objUpdated.documents;
            await api.put(`/filials/${id}`, objUpdated);
            setPageData({ ...pageData, ...objUpdated });
            setSuccessfullyUpdated(true);
            toast("Saved!", { autoClose: 1000 });
            handleOpened(null);
            setLoading(false);
          } catch (err) {
            console.log(err);
            toast(err.response.data.error, { type: "error", autoClose: 3000 });
            setLoading(false);
          }
        }
      } else {
        setLoading(false);
      }
    }
  }

  function handleCloseForm() {
    if (!successfullyUpdated) {
      toast("Changes discarted!", { autoClose: 1000 });
    }
    handleOpened(null);
  }

  function handleAddDiscount() {
    const newDiscounts = [
      ...pageData.discountlists,
      {
        id: null,
        name: null,
        type: null,
        value: 0,
        percent: 0,
        ponctuality_discount: false,
        all_installments: false,
        free_vacation: false,
        special_discount: false,
        active: true,
      },
    ];
    setPageData({ ...pageData, discountlists: newDiscounts });
    setSuccessfullyUpdated(false);
  }

  function handleAddShiftHour() {
    const newShiftHours = [
      ...pageData.shift_hours,
      {
        id: null,
        name: "",
        start_time: "",
        end_time: "",
      },
    ];
    setPageData({ ...pageData, shift_hours: newShiftHours });
    setSuccessfullyUpdated(false);
  }

  function handleRemoveShiftHour(index) {
    const newShiftHours = generalForm.current.getData();
    newShiftHours.shift_hours.splice(index, 1);
    generalForm.current.setData(newShiftHours);
    setPageData({
      ...pageData,
      shift_hours: [...newShiftHours.shift_hours],
    });
    setSuccessfullyUpdated(false);
  }

  function handleAddPlanSchedule() {
    const newPlanSchedules = [
      {
        id: null,
        temp_id: `temp_${Date.now()}_${Math.random()}`,
        period: "",
        active: true,
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
        start_time: "",
        end_time: "",
        is_global: false,
        processsubstatus_ids: [],
      },
      ...pageData.plan_schedules,
    ];
    setPageData({ ...pageData, plan_schedules: newPlanSchedules });
    setSuccessfullyUpdated(false);
  }

  function handleRemovePlanSchedule(index) {
    const newPlanSchedules = generalForm.current.getData();
    newPlanSchedules.plan_schedules.splice(index, 1);
    generalForm.current.setData(newPlanSchedules);
    setPageData({
      ...pageData,
      plan_schedules: [...newPlanSchedules.plan_schedules],
    });
    setSuccessfullyUpdated(false);
  }

  // Keeps is_global / processsubstatus_ids in pageData so they survive the
  // modal re-render and are picked up by the submit (which forces the array).
  function updatePlanScheduleSubstatusState(index, patch) {
    setPageData((prev) => {
      const plan_schedules = prev.plan_schedules.map((plan, i) =>
        i === index ? { ...plan, ...patch } : plan
      );
      return { ...prev, plan_schedules };
    });
    setSuccessfullyUpdated(false);
  }

  function handleConfirmSubstatusModal({ is_global, ids }) {
    // ALL (is_global) clears the links — backend receives an empty array.
    updatePlanScheduleSubstatusState(substatusModalIndex, {
      is_global,
      processsubstatus_ids: is_global ? [] : ids,
    });
    setSubstatusModalIndex(null);
  }

  function handleRemoveDiscount(index) {
    const newDiscounts = generalForm.current.getData();
    const removed = newDiscounts.discountlists.splice(index, 1);
    generalForm.current.setData(newDiscounts);
    setPageData({
      ...pageData,
      discountlists: [...newDiscounts.discountlists],
    });
    setSuccessfullyUpdated(false);
  }

  function handleDeleteDocument(id) {
    // const { file } = pageData.staffdocuments.find(staffdocument => staffdocument.id === id);
    alertBox({
      title: "Attention!",
      descriptionHTML: `<p>Are you sure you want to delete this file?</p>`,
      buttons: [
        {
          title: "No",
          class: "cancel",
        },
        {
          title: "Yes",
          onPress: async () => {
            try {
              await api.delete(`/filialdocuments/${id}`);
              toast("File deleted!", { autoClose: 1000 });
              setPageData({
                ...pageData,
                filialdocuments: pageData.filialdocuments.filter(
                  (filialdocument) => filialdocument.id !== id
                ),
              });
            } catch (err) {
              toast(err.response.data.error, {
                type: "error",
                autoClose: 3000,
              });
            }
          },
        },
      ],
    });
  }

  return (
    <Preview formType={formType} fullscreen={fullscreen}>
      {pageData ? (
        formType === "preview" ? (
          <div className="border h-full rounded-xl overflow-hidden flex flex-col justify-start gap-1 overflow-y-scroll">
            <div className="relative bg-gray-100 h-16 px-4 py-2 flex flex-row items-start justify-start">
              <button
                onClick={() => setFormType("full")}
                className="absolute top-2 right-20 text-md font-bold bg-mila_orange text-white rounded-md p-1 px-2 h-6 flex flex-row items-center justify-center text-xs gap-1"
              >
                <Pencil size={16} color="#fff" /> Open
              </button>
              <button
                onClick={() => handleOpened(null)}
                className="absolute top-2 right-2 text-md font-bold bg-secondary rounded-md p-1 px-2 h-6 flex flex-row items-center justify-center text-xs gap-1"
              >
                <X size={16} /> Close
              </button>
              <h2 style={{ fontSize: 24 }}>{pageData.name}</h2>
            </div>
            <div className="flex flex-1 flex-col items-left px-4 py-2 gap-1">
              <p className="border-b mb-1 pb-1">Branch Information</p>
              <div className="flex flex-row items-center gap-1 text-xs">
                <strong>Initials:</strong> {pageData.alias}
              </div>
              <div className="flex flex-row items-center gap-1 text-xs">
                <strong>Type:</strong>{" "}
                {pageData.Filialtype && pageData.Filialtype.name}
              </div>
              <div className="flex flex-row items-center gap-1 text-xs">
                <strong>Ein:</strong> {pageData.ein}
              </div>
            </div>
            <div className="flex flex-1 flex-col items-left px-4 py-2 gap-1">
              <p className="border-b mb-1 pb-1">Address Information</p>
              <div className="flex flex-row items-center gap-1 text-xs">
                <strong>Country:</strong> {pageData.country}
              </div>
              <div className="flex flex-row items-center gap-1 text-xs">
                <strong>State:</strong> {pageData.state}
              </div>
              <div className="flex flex-row items-center gap-1 text-xs">
                <strong>City:</strong> {pageData.city}
              </div>
              <div className="flex flex-row items-center gap-1 text-xs">
                <strong>Zip Code:</strong> {pageData.zipcode}
              </div>
              <div className="flex flex-row items-start gap-1 text-xs">
                <strong>Address:</strong> {pageData.address}
              </div>
            </div>
            <div className="flex flex-1 flex-col items-left px-4 py-2 gap-1">
              <p className="border-b mb-1 pb-1">Observations</p>
              <div
                readOnly
                className="flex flex-row items-start gap-1 text-xs h-24"
              >
                {pageData.observations}
              </div>
            </div>
            {pageData.shift_hours && pageData.shift_hours.length > 0 && (
              <div className="flex flex-1 flex-col items-left px-4 py-2 gap-1">
                <p className="border-b mb-1 pb-1">Shifts</p>
                <div className="flex flex-col gap-1 text-xs">
                  {pageData.shift_hours.map((shift) => (
                    <div
                      className="flex flex-row items-center gap-2"
                      key={`${shift.id || shift.name}-${shift.start_time}`}
                    >
                      <strong>{shift.name}</strong>
                      <span>
                        {shift.start_time} - {shift.end_time}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {pageData.plan_schedules && pageData.plan_schedules.length > 0 && (
              <div className="flex flex-1 flex-col items-left px-4 py-2 gap-1">
                <p className="border-b mb-1 pb-1">Plan Schedules</p>
                <div className="flex flex-col gap-1 text-xs">
                  {pageData.plan_schedules.map((plan, index) => (
                    <div
                      className="flex flex-col gap-1 border-b pb-2 mb-2"
                      key={`${plan.id || index}`}
                    >
                      <div className="flex flex-row items-center gap-2">
                        <strong>{plan.period}</strong>
                        <span>
                          {plan.start_time} - {plan.end_time}
                        </span>
                      </div>
                      <div className="flex flex-row items-center gap-2 text-gray-600">
                        {plan.monday && <span>Mon</span>}
                        {plan.tuesday && <span>Tue</span>}
                        {plan.wednesday && <span>Wed</span>}
                        {plan.thursday && <span>Thu</span>}
                        {plan.friday && <span>Fri</span>}
                        {plan.saturday && <span>Sat</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex flex-1 flex-col items-left px-4 py-2 gap-1">
              {/* <button type="button" className='bg-transparent border border-primary p-2 rounded-lg hover:bg-gray-100 text-primary flex flex-row items-center justify-center gap-2 font-bold text-sm'><Pencil size={14} color="#0B2870" /> Edit Filial</button> */}
            </div>
          </div>
        ) : (
          <div className="flex h-full flex-row items-start justify-between gap-4">
            <div className="flex flex-col items-center justify-between text-xs w-36 gap-4">
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="general"
                disabled={!hasTabPermission("general-tab")}
                messageOnDisabled={tabDisabledMessage("general-tab")}
              >
                <Building size={16} /> General
              </RegisterFormMenu>
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="shifts"
                disabled={!hasTabPermission("shifts-tab")}
                messageOnDisabled={tabDisabledMessage("shifts-tab")}
              >
                <Building size={16} /> Shifts
              </RegisterFormMenu>
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="plan-schedule"
                disabled={!hasTabPermission("plan-schedule-tab")}
                messageOnDisabled={tabDisabledMessage("plan-schedule-tab")}
              >
                <Building size={16} /> Plan Schedule
              </RegisterFormMenu>
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="price-list"
                messageOnDisabled={tabDisabledMessage(
                  "price-list-tab",
                  "Create the branch to have access to Price List."
                )}
                disabled={
                  id === "new" || !hasTabPermission("price-list-tab")
                }
              >
                <CircleDollarSign size={16} /> Price List
              </RegisterFormMenu>
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="discount-list"
                disabled={
                  id === "new" || !hasTabPermission("discount-list-tab")
                }
                messageOnDisabled={tabDisabledMessage(
                  "discount-list-tab",
                  "Create the branch to have access to Discount List."
                )}
              >
                <CircleDollarSign size={16} /> Discount List
              </RegisterFormMenu>
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="financial-support"
                disabled={
                  id === "new" || !hasTabPermission("financial-support-tab")
                }
                messageOnDisabled={tabDisabledMessage(
                  "financial-support-tab",
                  "Create the branch to have access to Financial Support."
                )}
              >
                <CircleDollarSign size={16} /> Financial Support
              </RegisterFormMenu>
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="contracts"
                disabled={id === "new" || !hasTabPermission("contracts-tab")}
                messageOnDisabled={tabDisabledMessage(
                  "contracts-tab",
                  "Create the branch to have access to Contracts."
                )}
              >
                <FileSignature size={16} /> Contracts
              </RegisterFormMenu>
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="contact-emails"
                disabled={
                  id === "new" || !hasTabPermission("contact-emails-tab")
                }
                messageOnDisabled={tabDisabledMessage(
                  "contact-emails-tab",
                  "Create the branch to have access to Contact E-mails."
                )}
              >
                <MailPlus size={16} /> Contact E-mails
              </RegisterFormMenu>
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="params"
                disabled={id === "new" || !hasTabPermission("params-tab")}
                messageOnDisabled={tabDisabledMessage(
                  "params-tab",
                  "Create the branch to have access to Params."
                )}
              >
                <Settings2 size={16} /> Params
              </RegisterFormMenu>
            </div>
            <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
              <div className="flex flex-col items-start justify-start text-sm overflow-y-scroll">
                <Form
                  ref={generalForm}
                  onSubmit={handleGeneralFormSubmit}
                  className="w-full"
                >
                  <InputContext.Provider
                    value={{
                      id,
                      generalForm,
                      setSuccessfullyUpdated,
                      fullscreen,
                      setFullscreen,
                      successfullyUpdated,
                      handleCloseForm,
                    }}
                  >
                    {id === "new" || pageData.loaded ? (
                      <>
                        <FormHeader
                          loading={loading}
                          access={access}
                          title={pageData.name}
                          registry={registry}
                          InputContext={InputContext}
                        />

                        <InputLineGroup
                          title="GENERAL"
                          activeMenu={
                            activeMenu === "general" && canOpenTab("general")
                          }
                        >
                          <Scope path={`administrator`}>
                            <InputLine title="Branch Administrator">
                              <Input
                                type="hidden"
                                name="id"
                                title="ID"
                                defaultValue={
                                  pageData.administrator
                                    ? pageData.administrator.id
                                    : null
                                }
                                InputContext={InputContext}
                              />
                              <Input
                                type="text"
                                name="name"
                                title="Name"
                                defaultValue={
                                  pageData.administrator
                                    ? pageData.administrator.name
                                    : ""
                                }
                                InputContext={InputContext}
                              />
                              <Input
                                type="text"
                                name="email"
                                title="E-mail"
                                grow
                                defaultValue={
                                  pageData.administrator
                                    ? pageData.administrator.email
                                    : ""
                                }
                                InputContext={InputContext}
                              />
                            </InputLine>
                          </Scope>

                          <InputLine title="General Data">
                            <Input
                              type="text"
                              name="ein"
                              required
                              title="EIN"
                              defaultValue={pageData.ein}
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="name"
                              required
                              title="Name"
                              grow
                              defaultValue={pageData.name}
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="alias"
                              onlyUpperCase
                              required
                              title="Alias"
                              shrink
                              defaultValue={pageData.alias}
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="sevis_school"
                              required
                              title="SEVIS School"
                              defaultValue={pageData.sevis_school}
                              InputContext={InputContext}
                            />
                            {/* <SelectPopover
                              name="active"
                              title="Active"
                              grow
                              options={[
                                { value: "Yes", label: "Yes" },
                                { value: "No", label: "No" },
                              ]}
                              defaultValue={{
                                value: pageData.active,
                                label: pageData.active ? "Yes" : "No",
                              }}
                              InputContext={InputContext}
                            /> */}
                          </InputLine>
                          <InputLine>
                            <Input
                              type="text"
                              name="i17_school_name"
                              title="I-17 School Name"
                              maxLength={255}
                              grow
                              defaultValue={pageData.i17_school_name || ""}
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <FindGeneric
                            route="filialtypes"
                            title="Filial Type"
                            scope="filialtype"
                            required
                            InputContext={InputContext}
                            defaultValue={{
                              id: pageData.filialtype_id,
                              name: pageData.Filialtype?.name,
                            }}
                            fields={[
                              {
                                title: "Name",
                                name: "name",
                              },
                            ]}
                          />

                          <InputLine title="Localization">
                            <SelectPopover
                              name="country"
                              title="Country"
                              required
                              grow
                              options={countriesOptions}
                              isSearchable
                              defaultValue={{
                                value: pageData.country,
                                label: pageData.country,
                              }}
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="state"
                              required
                              title="State"
                              grow
                              defaultValue={pageData.state}
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="city"
                              required
                              title="City"
                              grow
                              defaultValue={pageData.city}
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="zipcode"
                              isZipCode
                              grow
                              required
                              title="Zip Code"
                              defaultValue={pageData.zipcode}
                              placeholder="-----"
                              InputContext={InputContext}
                            />
                          </InputLine>

                          <InputLine>
                            <Textarea
                              type="text"
                              required
                              grow
                              name="address"
                              title="Address"
                              rows={5}
                              defaultValue={pageData.address}
                              InputContext={InputContext}
                            />
                          </InputLine>

                          <InputLine>
                            <FileInput
                              type="file"
                              name="parking_spot_image"
                              title="Parking Spot Image"
                              InputContext={InputContext}
                              grow
                            />
                            {pageData.parking_spot_image_file && (
                              <a
                                href={pageData.parking_spot_image_file.url}
                                target="_blank"
                                rel="noreferrer"
                              >
                                <img
                                  src={pageData.parking_spot_image_file.url}
                                  alt="Parking Spot Image"
                                  style={{
                                    maxHeight: "150px",
                                  }}
                                />
                              </a>
                            )}
                          </InputLine>

                          <InputLine title="Contact / On the web">
                            <PhoneNumberInput
                              type="text"
                              name="phone"
                              title="Phone"
                              value={pageData.phone}
                              InputContext={InputContext}
                            />
                            <PhoneNumberInput
                              type="text"
                              name="phone2"
                              title="Phone 2"
                              value={pageData.phone2}
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="email"
                              onlyLowerCase
                              grow
                              title="Email"
                              defaultValue={pageData.email}
                              placeholder="email@mila.usa"
                              InputContext={InputContext}
                            />
                          </InputLine>

                          <InputLine>
                            <PhoneNumberInput
                              type="text"
                              name="whatsapp"
                              title="Whatsapp"
                              value={pageData.whatsapp}
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="facebook"
                              onlyLowerCase
                              title="Facebook"
                              defaultValue={pageData.facebook}
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="instagram"
                              onlyLowerCase
                              title="Instagram"
                              defaultValue={pageData.instagram}
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="website"
                              onlyLowerCase
                              grow
                              title="Website"
                              defaultValue={pageData.website}
                              InputContext={InputContext}
                            />
                          </InputLine>

                          <InputLine title="Observations">
                            <Textarea
                              type="text"
                              name="observations"
                              rows={3}
                              grow
                              defaultValue={pageData.observations}
                              InputContext={InputContext}
                            />
                          </InputLine>
                        </InputLineGroup>

                        <InputLineGroup
                          title="SHIFTS"
                          activeMenu={
                            activeMenu === "shifts" && canOpenTab("shifts")
                          }
                        >
                          <h1 className="w-full border-b p-4 pb-0 pt-2 pb-2 font-bold">
                            Shifts
                          </h1>
                          {pageData.shift_hours &&
                            pageData.shift_hours.map((shift, index) => (
                              <Scope key={index} path={`shift_hours[${index}]`}>
                                <InputLine>
                                  <button
                                    type="button"
                                    className="mt-3 bg-none border-none"
                                    onClick={() => handleRemoveShiftHour(index)}
                                  >
                                    <Trash size={14} />
                                  </button>
                                  <Input
                                    type="hidden"
                                    name="id"
                                    defaultValue={shift.id}
                                    InputContext={InputContext}
                                  />
                                  <Input
                                    type="text"
                                    grow
                                    name="name"
                                    title="Name"
                                    defaultValue={shift.name}
                                    InputContext={InputContext}
                                  />
                                  <Input
                                    type="time"
                                    shrink
                                    name="start_time"
                                    title="Start"
                                    defaultValue={shift.start_time}
                                    InputContext={InputContext}
                                  />
                                  <Input
                                    type="time"
                                    shrink
                                    name="end_time"
                                    title="End"
                                    defaultValue={shift.end_time}
                                    InputContext={InputContext}
                                  />
                                </InputLine>
                                <div className="w-full border-b p-4 pt-2"></div>
                              </Scope>
                            ))}
                          <button
                            type="button"
                            onClick={handleAddShiftHour}
                            className="m-2 mt-4 text-md font-bold border border-mila_orange text-mila_orange rounded-md p-4 h-6 flex flex-row items-center justify-center text-xs gap-1"
                          >
                            + Add Shift
                          </button>
                        </InputLineGroup>

                        <InputLineGroup
                          title="PLAN SCHEDULE"
                          activeMenu={
                            activeMenu === "plan-schedule" &&
                            canOpenTab("plan-schedule")
                          }
                        >
                          <div className="w-full border-b p-4 pb-0 pt-2 pb-2 flex flex-row items-center justify-between">
                            <h1 className="font-bold">
                              Plan Schedules
                            </h1>
                            <button
                              type="button"
                              onClick={handleAddPlanSchedule}
                              className="text-md font-bold border border-mila_orange text-mila_orange rounded-md p-4 h-6 flex flex-row items-center justify-center text-xs gap-1"
                            >
                              + Add Plan Schedule
                            </button>
                          </div>
                          {pageData.plan_schedules &&
                            pageData.plan_schedules.map((plan, index) => (
                              <Scope key={plan.id || plan.temp_id || `new-plan-${index}`} path={`plan_schedules[${index}]`}>
                                <InputLine>
                                  <Input
                                    type="hidden"
                                    name="id"
                                    defaultValue={plan.id}
                                    InputContext={InputContext}
                                  />
                                  <SelectPopover
                                    name="period"
                                    title="Period"
                                    grow
                                    options={periodOptions}
                                    isSearchable
                                    defaultValue={periodOptions.find(
                                      (period) => period.value === plan.period
                                    )}
                                    InputContext={InputContext}
                                  />
                                  <Input
                                    type="time"
                                    shrink
                                    name="start_time"
                                    title="Start"
                                    defaultValue={plan.start_time}
                                    InputContext={InputContext}
                                  />
                                  <Input
                                    type="time"
                                    shrink
                                    name="end_time"
                                    title="End"
                                    defaultValue={plan.end_time}
                                    InputContext={InputContext}
                                  />
                                  <SelectPopover
                                    name="active"
                                    required
                                    title="Active"
                                    options={yesOrNoOptions}
                                    defaultValue={yesOrNoOptions.find(
                                      (type) => type.value === (plan.active ?? true)
                                    )}
                                    InputContext={InputContext}
                                  />
                                </InputLine>
                                <InputLine>
                                  <CheckboxInput
                                    name="monday"
                                    title="Monday"
                                    defaultValue={plan.monday}
                                    InputContext={InputContext}
                                  />
                                  <CheckboxInput
                                    name="tuesday"
                                    title="Tuesday"
                                    defaultValue={plan.tuesday}
                                    InputContext={InputContext}
                                  />
                                  <CheckboxInput
                                    name="wednesday"
                                    title="Wednesday"
                                    defaultValue={plan.wednesday}
                                    InputContext={InputContext}
                                  />
                                  <CheckboxInput
                                    name="thursday"
                                    title="Thursday"
                                    defaultValue={plan.thursday}
                                    InputContext={InputContext}
                                  />
                                  <CheckboxInput
                                    name="friday"
                                    title="Friday"
                                    defaultValue={plan.friday}
                                    InputContext={InputContext}
                                  />
                                  <CheckboxInput
                                    name="saturday"
                                    title="Saturday"
                                    defaultValue={plan.saturday}
                                    InputContext={InputContext}
                                  />
                                </InputLine>
                                <div className="flex flex-row items-center gap-2 px-4 pb-2">
                                  <span className="text-xs text-gray-500">
                                    Substatus:
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => setSubstatusModalIndex(index)}
                                    className="w-fit text-xs border border-mila_orange text-mila_orange rounded-md py-1 px-3 hover:bg-mila_orange hover:text-white"
                                  >
                                    {plan.is_global
                                      ? "ALL Substatus"
                                      : plan.processsubstatus_ids &&
                                        plan.processsubstatus_ids.length > 0
                                      ? `${plan.processsubstatus_ids.length} selected`
                                      : "Select Substatus"}
                                  </button>
                                </div>
                                <div className="w-full border-b p-4 pt-2"></div>
                              </Scope>
                            ))}
                        </InputLineGroup>

                        <InputLineGroup
                          title="PRICE LIST"
                          activeMenu={
                            activeMenu === "price-list" &&
                            canOpenTab("price-list")
                          }
                        >
                          <h1 className="w-full border-b p-4 pb-0 pt-2 pb-2 font-bold">
                            Price List
                          </h1>
                          {pageData.pricelists
                            .sort((a, b) =>
                              a.processsubstatuses
                                ? a.processsubstatuses.name
                                : a.name > b.processsubstatuses
                                ? b.processsubstatuses.name
                                : b.name
                            )
                            .map((price, index) => (
                              <Scope key={index} path={`pricelists[${index}]`}>
                                <InputLine>
                                  {/* {!price.id && <button type='button' className='mt-3 bg-none border-none' onClick={() => handleRemovePrice(index)}><Trash size={14} /></button>} */}
                                  <Input
                                    type="hidden"
                                    name={`id`}
                                    defaultValue={price.id}
                                    InputContext={InputContext}
                                  />
                                  <Input
                                    type="hidden"
                                    name={`processsubstatus_id`}
                                    defaultValue={
                                      price.processsubstatus_id || price.id
                                    }
                                    InputContext={InputContext}
                                  />
                                  <Input
                                    type="text"
                                    readOnly
                                    grow
                                    name={`name`}
                                    title="Process"
                                    defaultValue={
                                      price.processsubstatuses
                                        ? price.processsubstatuses.name
                                        : price.name
                                    }
                                    InputContext={InputContext}
                                  />
                                  <Input
                                    type="text"
                                    shrink
                                    name={`registration_fee`}
                                    title="Registration Fee"
                                    defaultValue={price.registration_fee || 0}
                                    InputContext={InputContext}
                                  />
                                  <Input
                                    type="text"
                                    shrink
                                    name={`book`}
                                    title="Book"
                                    defaultValue={price.book || 0}
                                    InputContext={InputContext}
                                  />
                                  <Input
                                    type="text"
                                    shrink
                                    name={`tuition`}
                                    title="Tuition"
                                    defaultValue={price.tuition || 0}
                                    InputContext={InputContext}
                                  />
                                  <SelectPopover
                                    name="tuition_in_advance"
                                    title="In Advance?"
                                    options={yesOrNoOptions}
                                    defaultValue={{
                                      value: price.tuition_in_advance
                                        ? "Yes"
                                        : "No",
                                      label: price.tuition_in_advance
                                        ? "Yes"
                                        : "No",
                                    }}
                                    InputContext={InputContext}
                                  />
                                  <SelectPopover
                                    name="active"
                                    title="Active"
                                    options={yesOrNoOptions}
                                    defaultValue={{
                                      value: price.active ? "Yes" : "No",
                                      label: price.active ? "Yes" : "No",
                                    }}
                                    InputContext={InputContext}
                                  />
                                </InputLine>
                                <div className="w-full border-b"></div>
                              </Scope>
                            ))}
                          {/* <button type="button" onClick={handleAddPrice} className={`m-2 mt-4 text-md font-bold border border-mila_orange text-mila_orange rounded-md p-4 h-6 flex flex-row items-center justify-center text-xs gap-1`}>
                                                    + Add Price
                                                </button> */}
                        </InputLineGroup>

                        <InputLineGroup
                          title="DISCOUNT LIST"
                          activeMenu={
                            activeMenu === "discount-list" &&
                            canOpenTab("discount-list")
                          }
                        >
                          <h1 className="w-full border-b p-4 pb-0 pt-2 pb-2 font-bold">
                            Discount List
                          </h1>
                          {pageData.discountlists &&
                            pageData.discountlists
                              .sort((a, b) => a.name > b.name)
                              .map((discount, index) => (
                                <Scope
                                  key={index}
                                  path={`discountlists[${index}]`}
                                >
                                  <InputLine>
                                    {!discount.id && (
                                      <button
                                        type="button"
                                        className="mt-3 bg-none border-none"
                                        onClick={() =>
                                          handleRemoveDiscount(index)
                                        }
                                      >
                                        <Trash size={14} />
                                      </button>
                                    )}
                                    <Input
                                      type="hidden"
                                      name={`id`}
                                      defaultValue={discount.id}
                                      InputContext={InputContext}
                                    />
                                    <SelectPopover
                                      name="type"
                                      grow
                                      required
                                      title="Type"
                                      options={discountTypesOptions}
                                      defaultValue={discountTypesOptions.find(
                                        (type) => type.value === discount.type
                                      )}
                                      InputContext={InputContext}
                                    />
                                    <Input
                                      type="text"
                                      grow
                                      name={`name`}
                                      required
                                      title="Name"
                                      defaultValue={discount.name}
                                      InputContext={InputContext}
                                    />
                                    <SelectPopover
                                      name="percent"
                                      grow
                                      required
                                      title="Discount"
                                      options={discountOptions}
                                      defaultValue={discountOptions.find(
                                        (type) =>
                                          type.value === discount.percent
                                      )}
                                      InputContext={InputContext}
                                    />
                                    <Input
                                      type="text"
                                      shrink
                                      name={`value`}
                                      required
                                      title="Amount"
                                      defaultValue={discount.value}
                                      InputContext={InputContext}
                                    />
                                    {/* <SelectPopover
                                      name="all_installments"
                                      required
                                      title="All Installments"
                                      options={yesOrNoOptions}
                                      defaultValue={yesOrNoOptions.find(
                                        (type) =>
                                          type.value ===
                                          discount.all_installments
                                      )}
                                      InputContext={InputContext}
                                    />
                                    <SelectPopover
                                      name="free_vacation"
                                      required
                                      title="Free Vacation"
                                      options={yesOrNoOptions}
                                      defaultValue={yesOrNoOptions.find(
                                        (type) =>
                                          type.value === discount.free_vacation
                                      )}
                                      InputContext={InputContext}
                                    /> */}

                                    <SelectPopover
                                      name="applied_at"
                                      required
                                      title="Applied At"
                                      options={appliedAtOptions}
                                      defaultValue={appliedAtOptions.find(
                                        (type) =>
                                          type.value === discount.applied_at
                                      )}
                                      InputContext={InputContext}
                                    />
                                    <SelectPopover
                                      name="active"
                                      required
                                      title="Active"
                                      options={yesOrNoOptions}
                                      defaultValue={yesOrNoOptions.find(
                                        (type) => type.value === discount.active
                                      )}
                                      InputContext={InputContext}
                                    />
                                  </InputLine>
                                  <div className="w-full border-b p-4 pt-2"></div>
                                </Scope>
                              ))}
                          <button
                            type="button"
                            onClick={handleAddDiscount}
                            className={`m-2 mt-4 text-md font-bold border border-mila_orange text-mila_orange rounded-md p-4 h-6 flex flex-row items-center justify-center text-xs gap-1`}
                          >
                            + Add Discount
                          </button>
                        </InputLineGroup>

                        <InputLineGroup
                          title="FINANCIAL SUPPORT"
                          activeMenu={
                            activeMenu === "financial-support" &&
                            canOpenTab("financial-support")
                          }
                        >
                          <h1 className="w-full border-b p-4 pb-0 pt-2 pb-2 font-bold">
                            Financial Support
                          </h1>
                          <InputLine>
                            <Input
                              type="text"
                              onlyFloat
                              grow
                              name={`financial_support_student_amount`}
                              title="Amount ($) per month required for students"
                              defaultValue={
                                pageData.financial_support_student_amount
                              }
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              onlyFloat
                              grow
                              name={`financial_support_dependent_amount`}
                              title="Amount ($) per month required for dependents"
                              defaultValue={
                                pageData.financial_support_dependent_amount
                              }
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              onlyFloat
                              grow
                              name={`financial_support_year_amount`}
                              title="Amount ($) per year required by MILA"
                              defaultValue={
                                pageData.financial_support_year_amount
                              }
                              InputContext={InputContext}
                            />
                          </InputLine>
                        </InputLineGroup>

                        <InputLineGroup
                          title="CONTRACTS"
                          activeMenu={
                            activeMenu === "contracts" &&
                            canOpenTab("contracts")
                          }
                        >
                          {pageData.documents &&
                            pageData.documents.length > 0 &&
                            pageData.documents.map((document, index) => {
                              return (
                                <Scope key={index} path={`documents[${index}]`}>
                                  <Input
                                    type="hidden"
                                    name="document_id"
                                    defaultValue={document.id}
                                    InputContext={InputContext}
                                  />
                                  <InputLine title={document.title}>
                                    {!document.multiple &&
                                      pageData.filialdocuments &&
                                      pageData.filialdocuments.filter(
                                        (filialdocument) =>
                                          filialdocument.document_id ===
                                          document.id
                                      ).length === 0 && (
                                        <FileInput
                                          type="file"
                                          name="file_id"
                                          title={"File"}
                                          grow
                                          InputContext={InputContext}
                                        />
                                      )}
                                    {document.multiple && (
                                      <FileInputMultiple
                                        type="file"
                                        name="file_id"
                                        title={"Multiple Files"}
                                        grow
                                        InputContext={InputContext}
                                      />
                                    )}
                                  </InputLine>
                                  {pageData.filialdocuments &&
                                    pageData.filialdocuments.length > 0 && (
                                      <InputLine subtitle="Attached Files">
                                        <div className="flex flex-col justify-center items-start gap-4">
                                          {pageData.filialdocuments &&
                                            pageData.filialdocuments.map(
                                              (filialdocument, index) => {
                                                if (
                                                  filialdocument.document_id ===
                                                    document.id &&
                                                  filialdocument.file
                                                ) {
                                                  return (
                                                    <>
                                                      <div className="flex flex-row justify-center items-center gap-2">
                                                        <a
                                                          href={
                                                            filialdocument.file
                                                              .url
                                                          }
                                                          target="_blank"
                                                          className="text-xs"
                                                        >
                                                          <div
                                                            className="flex flex-row items-center border px-4 py-2 gap-1 rounded-md bg-gray-100 hover:border-gray-300"
                                                            key={index}
                                                          >
                                                            <Files size={16} />
                                                            {
                                                              filialdocument
                                                                .file.name
                                                            }
                                                          </div>
                                                        </a>
                                                        <button
                                                          type="button"
                                                          onClick={() =>
                                                            handleDeleteDocument(
                                                              filialdocument.id
                                                            )
                                                          }
                                                          className="text-xs text-red-700 cursor-pointer flex flex-row items-center justify-start gap-1 mt-1 px-2 py-1 rounded-sm hover:bg-red-100"
                                                        >
                                                          <X size={12} /> Delete
                                                        </button>
                                                      </div>
                                                    </>
                                                  );
                                                }
                                              }
                                            )}
                                        </div>
                                      </InputLine>
                                    )}
                                </Scope>
                              );
                            })}
                        </InputLineGroup>

                        <InputLineGroup
                          title="Contact E-mails"
                          activeMenu={
                            activeMenu === "contact-emails" &&
                            canOpenTab("contact-emails")
                          }
                        >
                          <InputLine title="Immigration and I-20 related questions">
                            <Input
                              type="text"
                              grow
                              name="email_immigration"
                              title="E-mails (separated by commas)"
                              defaultValue={pageData.email_immigration}
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <InputLine title="DSO responsible for transfers">
                            <Input type="text" grow name="email_transfer_dso"
                              title="E-mails (separated by commas)"
                              defaultValue={pageData.email_transfer_dso}
                              InputContext={InputContext} />
                          </InputLine>
                          <InputLine title="Vacations, medical excuses, and absences">
                            <Input
                              type="text"
                              grow
                              name="email_vacations_me"
                              title="E-mails (separated by commas)"
                              defaultValue={pageData.email_vacations_me}
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <InputLine title="Travel authorization and I-20 extensions">
                            <Input
                              type="text"
                              grow
                              name="email_travel_authorization"
                              title="E-mails (separated by commas)"
                              defaultValue={pageData.email_travel_authorization}
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <InputLine title="Academic Department">
                            <Input
                              type="text"
                              grow
                              name="email_academic"
                              title="E-mails (separated by commas)"
                              defaultValue={pageData.email_academic}
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <InputLine title="Financial Department">
                            <Input
                              type="text"
                              grow
                              name="email_financial"
                              title="E-mails (separated by commas)"
                              defaultValue={pageData.email_financial}
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <InputLine title="General information">
                            <Input
                              type="text"
                              grow
                              name="email_general"
                              title="E-mails (separated by commas)"
                              defaultValue={pageData.email_general}
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <InputLine title="MILA App">
                            <Input
                              type="text"
                              grow
                              name="email_app"
                              title="E-mails (separated by commas)"
                              defaultValue={pageData.email_app}
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <InputLine title="MILA IT">
                            <Input
                              type="text"
                              grow
                              name="email_it"
                              title="E-mails (separated by commas)"
                              defaultValue={pageData.email_it}
                              InputContext={InputContext}
                            />
                          </InputLine>
                        </InputLineGroup>

                        <InputLineGroup
                          title="Params"
                          activeMenu={
                            activeMenu === "params" && canOpenTab("params")
                          }
                        >
                          <InputLine title="Registration Number Mode">
                            <SelectPopover
                              name="registration_number_mode"
                              title="Mode"
                              grow
                              InputContext={InputContext}
                              options={[
                                { value: "MANUAL", label: "Manual" },
                                { value: "AUTOMATIC", label: "Automatic" },
                              ]}
                              defaultValue={
                                pageData.registration_number_mode
                                  ? {
                                      value: pageData.registration_number_mode,
                                      label:
                                        pageData.registration_number_mode ===
                                        "AUTOMATIC"
                                          ? "Automatic"
                                          : "Manual",
                                    }
                                  : null
                              }
                            />
                          </InputLine>
                          <InputLine title="Overdue Invoice Block">
                            <SelectPopover
                              name="overdue_invoice_block_enabled"
                              title="Enable overdue invoice block?"
                              grow
                              options={yesOrNoOptions}
                              defaultValue={
                                overdueInvoiceBlockEnabled
                                  ? yesOrNoOptions[0]
                                  : yesOrNoOptions[1]
                              }
                              setReturnPopover={(opt) =>
                                setOverdueInvoiceBlockEnabled(
                                  Boolean(opt?.value)
                                )
                              }
                              InputContext={InputContext}
                            />
                            {overdueInvoiceBlockEnabled && (
                              <Input
                                type="text"
                                name="overdue_invoice_limit"
                                onlyInt
                                title="How many invoices?"
                                shrink
                                defaultValue={pageData.overdue_invoice_limit}
                                InputContext={InputContext}
                              />
                            )}
                          </InputLine>
                          <InputLine title="Settlement Range Date">
                            <Input
                              type="text"
                              name="settlement_range_date"
                              title="Settlement Range Date"
                              grow
                              onlyInt
                              defaultValue={pageData.settlement_range_date}
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <InputLine title="MILA Global Price Per Hour">
                            <Input
                              type="text"
                              name="price_by_hour"
                              title="Price per hour ($) - leave empty or 0 to use the default"
                              grow
                              onlyFloat
                              defaultValue={pageData.price_by_hour}
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <InputLine title="HR Days Limits">
                            <Input
                              type="text"
                              name="vacation_days_allowed"
                              title="Vacation Days Allowed"
                              grow
                              onlyInt
                              defaultValue={pageData.vacation_days_allowed}
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="sick_days_allowed"
                              title="Sick Days Allowed"
                              grow
                              onlyInt
                              defaultValue={pageData.sick_days_allowed}
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <InputLine title="Orientation Address">
                            <Input
                              type="text"
                              name="orientation_address"
                              title="Address"
                              grow
                              defaultValue={pageData.orientation_address}
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <InputLine title="DSO Name">
                            <Input
                              type="text"
                              name="dso_name"
                              title="DSO Name"
                              grow
                              defaultValue={pageData.dso_name}
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <InputLine title="DSO Signature">
                            <FileInput
                              type="file"
                              name="dso_signature"
                              title="Upload the file"
                              InputContext={InputContext}
                              grow
                            />
                            {pageData.dso_signature_file && (
                              <a
                                href={pageData.dso_signature_file.url}
                                target="_blank"
                                className="text-xs mt-4"
                              >
                                <div className="flex flex-row items-center border px-4 py-2 gap-1 rounded-md bg-gray-100 hover:border-gray-300">
                                  <Files size={16} />
                                  {pageData.dso_signature_file.name}
                                </div>
                              </a>
                            )}
                          </InputLine>
                          <InputLine title="Student Handbook">
                            <FileInput
                              type="file"
                              name="student_handbook"
                              title="Upload the file"
                              InputContext={InputContext}
                              grow
                            />
                            {pageData.student_handbook && (
                              <a
                                href={pageData.student_handbook.url}
                                target="_blank"
                                className="text-xs mt-4"
                              >
                                <div className="flex flex-row items-center border px-4 py-2 gap-1 rounded-md bg-gray-100 hover:border-gray-300">
                                  <Files size={16} />
                                  {pageData.student_handbook.name}
                                </div>
                              </a>
                            )}
                          </InputLine>
                          <InputLine title="MILA App First Access">
                            <FileInput
                              type="file"
                              name="mila_app_first_access"
                              title="Upload the file"
                              InputContext={InputContext}
                              grow
                            />
                            {pageData.mila_app_first_access && (
                              <a
                                href={pageData.mila_app_first_access.url}
                                target="_blank"
                                className="text-xs mt-4"
                              >
                                <div className="flex flex-row items-center border px-4 py-2 gap-1 rounded-md bg-gray-100 hover:border-gray-300">
                                  <Files size={16} />
                                  {pageData.mila_app_first_access.name}
                                </div>
                              </a>
                            )}
                          </InputLine>
                        </InputLineGroup>
                      </>
                    ) : (
                      <FormLoading />
                    )}
                  </InputContext.Provider>
                </Form>
              </div>
            </div>
          </div>
        )
      ) : null}
      {substatusModalIndex !== null && (
        <PlanScheduleSubstatusModal
          options={processSubstatusOptions}
          selectedIds={
            pageData.plan_schedules?.[substatusModalIndex]
              ?.processsubstatus_ids || []
          }
          isGlobal={
            pageData.plan_schedules?.[substatusModalIndex]?.is_global || false
          }
          onClose={() => setSubstatusModalIndex(null)}
          onConfirm={handleConfirmSubstatusModal}
        />
      )}
    </Preview>
  );
}
